import { useCallback, useEffect, useMemo, useState } from 'react';

export const QUICK_TAGS_STORAGE_KEY = 'images_search_common_tags_v1';

export const DEFAULT_QUICK_TAGS: string[] = [
  '抛釉砖',
  '全抛釉',
  '仿古砖',
  '大理石纹',
  '木纹砖',
  '水泥灰',
  '哑光',
  '亮光',
  '通体砖',
  '微晶石',
  '马赛克',
  '地面砖',
  '墙砖',
  '瓷片',
  '仿石纹'
];

function readStoredQuickTags(): string[] {
  try {
    const raw = localStorage.getItem(QUICK_TAGS_STORAGE_KEY);
    if (!raw) return DEFAULT_QUICK_TAGS.slice();
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return parsed.map((x) => String(x)).filter(Boolean);
    }
    return DEFAULT_QUICK_TAGS.slice();
  } catch {
    return DEFAULT_QUICK_TAGS.slice();
  }
}

function writeStoredQuickTags(tags: string[]) {
  try {
    localStorage.setItem(QUICK_TAGS_STORAGE_KEY, JSON.stringify(tags));
  } catch {}
}

function normalizeTags(tags: string[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const t of tags) {
    const v = (t || '').trim();
    if (!v) continue;
    if (seen.has(v)) continue;
    seen.add(v);
    result.push(v);
  }
  return result;
}

export function useQuickTags(initialDefaults?: string[]) {
  const initial = useMemo(() => {
    if (Array.isArray(initialDefaults) && initialDefaults.length) {
      return normalizeTags(initialDefaults);
    }
    return readStoredQuickTags();
  }, [initialDefaults]);

  const [quickTags, setQuickTags] = useState<string[]>(initial);

  useEffect(() => {
    // 初始化：若本地没有存储，则写入默认
    const stored = readStoredQuickTags();
    if (stored && Array.isArray(stored)) {
      setQuickTags(normalizeTags(stored));
    }
  }, []);

  useEffect(() => {
    writeStoredQuickTags(normalizeTags(quickTags));
  }, [quickTags]);

  const replace = useCallback((next: string[]) => {
    setQuickTags(normalizeTags(next));
  }, []);

  const add = useCallback((tag: string) => {
    const v = (tag || '').trim();
    if (!v) return;
    setQuickTags((prev) => normalizeTags([...prev, v]));
  }, []);

  const addMany = useCallback((tags: string[]) => {
    setQuickTags((prev) => normalizeTags([...prev, ...tags]));
  }, []);

  const remove = useCallback((tag: string) => {
    setQuickTags((prev) => prev.filter((t) => t !== tag));
  }, []);

  const updateAt = useCallback((index: number, value: string) => {
    setQuickTags((prev) => {
      const copy = prev.slice();
      if (index < 0 || index >= copy.length) return prev;
      copy[index] = value;
      return normalizeTags(copy);
    });
  }, []);

  const move = useCallback((from: number, to: number) => {
    setQuickTags((prev) => {
      const copy = prev.slice();
      if (from < 0 || from >= copy.length || to < 0 || to >= copy.length) return prev;
      const [item] = copy.splice(from, 1);
      copy.splice(to, 0, item);
      return copy;
    });
  }, []);

  const reset = useCallback(() => {
    setQuickTags(DEFAULT_QUICK_TAGS.slice());
  }, []);

  const exists = useCallback((tag: string) => {
    return quickTags.includes((tag || '').trim());
  }, [quickTags]);

  return [quickTags, { replace, add, addMany, remove, updateAt, move, reset, exists }] as const;
}















